import React from 'react'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Users, User } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { leaveService } from '@/services/leaveService'
import { useAuthStore } from '@/store/authStore'

const EmployeesOnLeaveCard = () => {
  const { user } = useAuthStore()
  
  // Fetch employees on leave today
  const { data: leaveData, isLoading, error } = useQuery(
    ['employeesOnLeaveToday'],
    () => leaveService.getEmployeesOnLeaveToday(),
    {
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
      onError: (err) => {
        toast.error('Failed to fetch employees on leave: ' + (err.response?.data?.error || err.message))
      }
    }
  )

  const data = leaveData?.data

  if (error) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-destructive">Failed to load employees on leave</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-100">
            <Users className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold">Employees on Leave</CardTitle>
            <p className="text-sm text-muted-foreground">
              {data?.date ? dayjs(data.date).format('MMMM DD, YYYY') : 'Today'}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        ) : data?.employees?.length === 0 ? (
          <EmptyState
            icon="users"
            title="No employees on leave"
            description="All team members are present today"
          />
        ) : (
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-4">
              <span className="text-sm font-medium text-orange-700">
                {data?.count || 0} employee{data?.count !== 1 ? 's' : ''} on leave
              </span>
            </div>
            
            <div className="space-y-3">
              {data?.employees?.map((leave, index) => {
                const isCurrentUser = leave.employee.email === user?.email
                return (
                  <div 
                    key={index} 
                    className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                      isCurrentUser ? 'bg-emerald-50 border border-emerald-100' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                      isCurrentUser ? 'bg-emerald-100' : 'bg-primary/10'
                    }`}>
                      <User className={`h-4 w-4 ${
                        isCurrentUser ? 'text-emerald-600' : 'text-primary'
                      }`} />
                    </div>
                    <span className={`text-sm ${
                      isCurrentUser ? 'font-semibold text-emerald-700' : 'text-foreground'
                    }`}>
                      {isCurrentUser ? 'You' : leave.employee.name}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default EmployeesOnLeaveCard
